/**
 * React Query hook for page config CRUD via Control Plane proxy.
 *
 * Fetches the current page config for editing, and provides a mutation
 * to publish changes back to the Tenant Server.
 */

import { useQuery, useMutation, keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  cpGetPageConfig,
  cpPublishPageConfig,
  type PublishPageConfigPayload,
} from '@/lib/api/controlPlanePageConfigClient'
import { useAuthStore } from '@/lib/store/authStore'

export const PAGE_CONFIG_QUERY_KEY = ['page-config'] as const

export function usePageConfig(pageId: string = 'home', storefrontKey?: string | null) {
  const queryClient = useQueryClient()
  const currentTenant = useAuthStore((s) => s.currentTenant)

  const configQuery = useQuery({
    queryKey: [...PAGE_CONFIG_QUERY_KEY, currentTenant, storefrontKey ?? '__primary__', pageId],
    queryFn: () => cpGetPageConfig(pageId, storefrontKey ?? undefined),
    placeholderData: keepPreviousData,
    enabled: !!currentTenant,
  })

  const publishMutation = useMutation({
    mutationFn: (payload: PublishPageConfigPayload) => cpPublishPageConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAGE_CONFIG_QUERY_KEY })
      toast.success('เผยแพร่แล้ว!')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'เผยแพร่ไม่สำเร็จ กรุณาลองอีกครั้ง')
    },
  })

  return {
    config: configQuery.data ?? null,
    isLoading: configQuery.isLoading,
    publish: publishMutation.mutate,
    isPublishing: publishMutation.isPending,
  }
}
